import API from "@/API";
import moment from "@/utils/moment";
import loader from "../../creators/loader";

const SET_TITLE = "SET_TITLE";
const SET_MESSAGE = "SET_MESSAGE";
const SET_DATES = "SET_DATES";
const SET_TIME = "SET_TIME";
const SET_CITIES = "SET_CITIES";
const SET_USERS = "SET_USERS";
const SET_ACCOUNT_STATUS = "SET_ACCOUNT_STATUS";
const SET_ACCOUNTABLE_STATUS = "SET_ACCOUNTABLE_STATUS";
const SET_STATES = "SET_STATES";
const SET_DISTRICTS = "SET_DISTRICTS";
const SET_SCHEDULE = "SET_SCHEDULE";
const SET_TIME_ZONE = "SET_TIME_ZONE";
const SET_STEP = "SET_STEP";
const SET_STEP_ERROR = "SET_STEP_ERROR";
const SET_USER_TYPE = "SET_USER_TYPE";
const SET_VALID_NOTIFICATION = "SET_VALID_NOTIFICATION";

const getNotificationRoute = userType => {
  switch (userType) {
    case "Physical":
      return "physicals";
    case "Legal":
      return "legals";
    case "Professional":
      return "professionals";
    default:
      return "";
  }
};

const initialState = () => {
  return {
    title: "",
    message: "",
    dates: [],
    time: "",
    userType: "",
    states: [],
    cities: [],
    districts: [],
    users: [],
    accountStatus: [],
    accountableStatus: [],
    schedule: false,
    timeZone: "Brasilia",
    timeZoneOptions: [
      {
        value: "Brasilia",
        text: "America/Sao_Paulo"
      }
    ],
    step: 1,
    steps: [
      {
        name: "data",
        title: "Dados",
        component: "notification-data",
        hasError: false
      },
      {
        name: "users",
        title: "Usuários",
        component: "users",
        hasError: false
      },
      {
        name: "states",
        title: "Estados",
        component: "states",
        hasError: false
      },
      {
        name: "cities",
        title: "Cidades",
        component: "cities",
        hasError: false
      },
      {
        name: "districts",
        title: "Bairros",
        component: "districts",
        hasError: false
      },
      {
        name: "scheduling",
        title: "Agendar envio",
        component: "scheduling",
        hasError: false
      }
    ],
    validNotification: false
  };
};

export default {
  namespaced: true,
  state: { ...initialState(), ...loader.state },

  actions: {
    ...loader.actions,

    setTitle({ commit }, title) {
      commit("SET_TITLE", title);
    },
    setMessage({ commit }, message) {
      commit("SET_MESSAGE", message);
    },
    setAccountStatus({ commit }, accountStatus) {
      commit("SET_ACCOUNT_STATUS", accountStatus);
    },
    setAccountableStatus({ commit }, accountableStatus) {
      commit("SET_ACCOUNTABLE_STATUS", accountableStatus);
    },
    setDates({ commit }, dates) {
      commit("SET_DATES", dates);
    },
    setTime({ commit }, time) {
      commit("SET_TIME", time);
    },
    setStates({ commit }, states) {
      commit("SET_STATES", states);
    },
    setCities({ commit }, cities) {
      commit("SET_CITIES", cities);
    },
    setDistricts({ commit }, districts) {
      commit("SET_DISTRICTS", districts);
    },
    setUserType({ commit }, userType) {
      commit("SET_USER_TYPE", userType);
    },
    setUsers({ commit }, users) {
      commit("SET_USERS", users);
    },
    setSchedule({ commit }, schedule) {
      commit("SET_SCHEDULE", schedule);
    },
    setTimeZone({ commit }, timeZone) {
      commit("SET_TIME_ZONE", timeZone);
    },
    setStep({ commit }, step) {
      commit("SET_STEP", step);
    },
    setStepError({ commit }, stepName) {
      commit("SET_STEP_ERROR", stepName);
    },
    getTimestamp({ state }, date) {
      if (!state.time) return;

      let [hour, minute] = state.time.split(":");
      return moment(date)
        .hour(hour)
        .minute(minute)
        .second(0)
        .toISOString();
    },
    isValidNotification({ state, commit }) {
      let valid = state.title.length >= 1 && state.message.length >= 1;
      commit("SET_VALID_NOTIFICATION", valid);
    },
    async buildData({ state, dispatch }, date) {
      let data = {
        title: state.title,
        message: state.message,
        account_status: state.accountStatus,
        accountable_status: state.accountableStatus,
        states: state.states,
        cities: state.cities,
        districts: state.districts,
        users: state.users,
        user_type: getNotificationRoute(state.userType)
      };

      if (!date) return data;

      let timestamp = await dispatch("getTimestamp", date);
      if (timestamp) data["timestamp"] = timestamp;

      return data;
    },
    async send({ state, dispatch }) {
      dispatch("startLoader");

      try {
        if (state.title == "" || state.message == "" || state.userType == "") {
          dispatch("stopLoader");
          dispatch("setStep", 1);
        } else {
          if (state.dates.length < 1) {
            let data = await dispatch("buildData");

            await API.Notifications.create(data);
          } else {
            await state.dates.forEach(async date => {
              let data = await dispatch("buildData", date);
              await API.Notifications.create(data);
            });
          }
        }
      } catch (e) {
        dispatch("stopLoader");
        throw e;
      }

      dispatch("stopLoader");
    },
    clearState({ commit }) {
      commit("SET_STEP", 1);
      commit("SET_TITLE", "");
      commit("SET_MESSAGE", "");
      commit("SET_DATES", []);
      commit("SET_TIME", "");
      commit("SET_CITIES", []);
      commit("SET_USERS", []);
      commit("SET_ACCOUNT_STATUS", []);
      commit("SET_ACCOUNTABLE_STATUS", []);
      commit("SET_STATES", []);
      commit("SET_DISTRICTS", []);
      commit("SET_SCHEDULE", false);
    }
  },

  mutations: {
    ...loader.mutations,

    [SET_TITLE](state, title) {
      state.title = title;
    },
    [SET_MESSAGE](state, message) {
      state.message = message;
    },
    [SET_ACCOUNT_STATUS](state, accountStatus) {
      state.accountStatus = accountStatus;
    },
    [SET_ACCOUNTABLE_STATUS](state, accountableStatus) {
      state.accountableStatus = accountableStatus;
    },
    [SET_DATES](state, dates) {
      state.dates = dates;
    },
    [SET_TIME](state, time) {
      state.time = time;
    },
    [SET_STATES](state, states) {
      state.states = states;
    },
    [SET_CITIES](state, cities) {
      state.cities = cities;
    },
    [SET_DISTRICTS](state, districts) {
      state.districts = districts;
    },
    [SET_USERS](state, users) {
      state.users = users;
    },
    [SET_USER_TYPE](state, userType) {
      state.userType = userType;
    },
    [SET_SCHEDULE](state, schedule) {
      state.schedule = schedule;
    },
    [SET_TIME_ZONE](state, timeZone) {
      state.timeZone = timeZone;
    },
    [SET_STEP](state, step) {
      state.step = step;
    },
    [SET_STEP_ERROR](state, stepName) {
      let stepIndex = state.steps.findIndex(el => el.name === stepName);
      state.steps[stepIndex].error = true;
    },
    [SET_VALID_NOTIFICATION](state, validNotification) {
      state.validNotification = validNotification;
    }
  },
  getters: {}
};



// WEBPACK FOOTER //
// ./src/store/modules/notifications/send.js